/**
 * Sous-composant — liste des entreprises dont ce courtier est
 * `primary_broker_id`.
 *
 * Consomme `GET /v1/companies?broker_id=:id` qui existe déjà côté API
 * (cf. routes/companies.ts ligne 55). Read-only ; le détachement de
 * l'assignation passe par CompanyDetailPage (8.C, dette `null` documentée).
 *
 * Pas de pagination ici : on affiche les 50 premières companies. Si un
 * broker a plus de 50 companies, on lui propose un lien "Voir toutes"
 * vers /companies?broker_id=:id (filtre URL pas encore branché côté
 * CompanyListPage — TODO 8.C+).
 */

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useCompanies } from "../../../lib/queries/companies";

export function BrokerCompaniesList({ brokerId }: { brokerId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useCompanies({
    broker_id: brokerId,
    status: "all",
    limit: 50,
  });

  if (isLoading && !data) {
    return <p className="text-sm text-slate-400">{t("app.loading")}</p>;
  }
  if (!data || data.items.length === 0) {
    return <p className="text-sm text-slate-500">{t("brokers.detail.no_companies")}</p>;
  }

  return (
    <ul className="divide-y divide-slate-100">
      {data.items.map((c) => (
        <li key={c.id} className="py-2">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <Link
                to={`/companies/${c.id}`}
                className="font-medium text-brand-700 hover:underline"
              >
                {c.legal_name}
              </Link>
              {c.trade_name && c.trade_name !== c.legal_name && (
                <div className="truncate text-xs text-slate-500">{c.trade_name}</div>
              )}
            </div>
            <div className="text-right text-xs text-slate-500">
              {c.address_city ?? "—"}
              {c.address_province ? `, ${c.address_province}` : ""}
              <div className="mt-0.5">
                {c.status === "active" ? (
                  <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                    {t("companies.filters.active")}
                  </span>
                ) : (
                  <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">
                    {t("companies.filters.archived")}
                  </span>
                )}
              </div>
            </div>
          </div>
        </li>
      ))}
      {data.next_cursor && (
        <li className="pt-3 text-xs text-slate-500">
          {t("brokers.detail.more_companies_hint")}
        </li>
      )}
    </ul>
  );
}
